import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useLang } from "@/context/LangContext";
import { MultiLangField } from "@/components/admin/Fields";

const SECTIONS = ["hero", "stats", "about", "packages"];

const ToggleRow = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center justify-between gap-4 rounded-xl border border-border px-4 py-3">
    <span className="text-sm font-semibold">{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
  </label>
);

export const SystemSettings = ({ draft, set }) => {
  const { t } = useLang();
  const stats = draft.stats || [];
  const setStat = (i, patch) => set("stats", stats.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  return (
    <div className="space-y-8">
      <div className="grid gap-4 md:grid-cols-2">
        <ToggleRow label={t("registration_open")} checked={draft.registration_open} onChange={(v) => set("registration_open", v)} testId="admin-settings-registration-toggle" />
        <div className="flex items-center justify-between gap-4 rounded-xl border border-border px-4 py-3">
          <span className="text-sm font-semibold">{t("accent_color")}</span>
          <div className="flex items-center gap-2">
            <input type="color" value={draft.accent_color || "#15803d"} onChange={(e) => set("accent_color", e.target.value)} data-testid="admin-settings-accent-color" className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent" />
            <button type="button" onClick={() => set("accent_color", "")} data-testid="admin-settings-accent-reset" className="rounded-full border border-border px-3 py-1 text-xs font-semibold hover:bg-muted">{t("reset")}</button>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <Label className="text-sm font-bold">{t("sections_visibility")}</Label>
        <div className="grid gap-3 md:grid-cols-2">
          {SECTIONS.map((k) => (
            <ToggleRow key={k} label={t(`section_${k}`)} checked={draft.sections?.[k]} onChange={(v) => set("sections", { ...draft.sections, [k]: v })} testId={`admin-settings-section-${k}-toggle`} />
          ))}
        </div>
      </div>
      <div className="space-y-4">
        <Label className="text-sm font-bold">{t("stats_items")}</Label>
        {stats.map((s, i) => (
          <div key={s.id || `stat-${i}`} className="space-y-3 rounded-2xl border border-border p-4">
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1"><span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("stat_value")}</span><Input value={s.value} onChange={(e) => setStat(i, { value: e.target.value })} data-testid={`admin-stat-value-${i}`} /></div>
              <button type="button" onClick={() => set("stats", stats.filter((_, idx) => idx !== i))} data-testid={`admin-stat-remove-${i}`} className="flex h-10 w-10 items-center justify-center rounded-full border border-rose-300 text-rose-600 hover:bg-rose-50 dark:border-rose-800 dark:hover:bg-rose-950"><Trash2 className="h-4 w-4" /></button>
            </div>
            <MultiLangField value={s.label} onChange={(v) => setStat(i, { label: v })} testPrefix={`admin-stat-label-${i}`} />
          </div>
        ))}
        <button type="button" onClick={() => set("stats", [...stats, { id: crypto.randomUUID().slice(0, 8), value: "0", label: { en: "", am: "", om: "" } }])} data-testid="admin-stat-add-button" className="inline-flex items-center gap-2 rounded-full border border-dashed border-primary px-5 py-2.5 text-sm font-bold text-primary hover:bg-primary/5">
          <Plus className="h-4 w-4" />{t("add_stat")}
        </button>
      </div>
    </div>
  );
};
